/* =========================================================================
   JOMIKUTO · theme.js
   Core utilities: pub/sub, cart fetch helpers, quantity input custom element
   ========================================================================= */

window.Jomi = window.Jomi || {};

/* ---------- Money formatting (currency-aware, respects shop.money_format) ---------- */
/*
 * Single source of truth for formatting cent amounts from Shopify's cart AJAX API.
 *
 * Strategy: parse Shopify's money_format template string directly. This avoids
 * depending on window.Shopify.formatMoney which isn't reliably available on every
 * page and can throw inside try-catch in ways that break cart AJAX flows.
 *
 * Supports templates like:
 *   ₦{{amount_with_comma_separators}}
 *   ${{amount}}
 *   €{{amount_with_space_separator}}
 *   NGN {{amount}}
 *
 * v45 fix: v44 attempted to use Shopify.formatMoney which isn't always available;
 * on /cart page AJAX it could throw/hang and prevent cart render from resolving.
 */
Jomi.formatMoney = function(cents) {
  const c = typeof cents === 'string' ? parseInt(cents, 10) : cents;
  if (typeof c !== 'number' || isNaN(c)) return '';

  const format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';

  // Parse {{amount_*}} placeholder style
  const placeholder = /\{\{\s*(amount(_no_decimals|_with_comma_separators|_no_decimals_with_comma_separators|_with_apostrophe_separator|_no_decimals_with_space_separator|_with_space_separator|_with_period_and_space_separator)?)\s*\}\}/;
  const match = format.match(placeholder);

  let formatted;
  if (match) {
    const type = match[1];
    formatted = formatAmount(c, type);
    return format.replace(placeholder, formatted);
  }

  // No recognizable placeholder — fallback to Intl or numeric
  try {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const locale = (window.Shopify && Shopify.locale) || document.documentElement.lang || 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(c / 100);
  } catch (e) {
    return (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function formatAmount(cents, type) {
    const value = cents / 100;
    const thousand = (num, sep) => {
      const [whole, dec] = num.toFixed(2).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + '.' + dec;
    };
    const thousandNoDec = (num, sep) => {
      return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    };
    switch (type) {
      case 'amount':
        return value.toFixed(2);
      case 'amount_no_decimals':
        return Math.round(value).toString();
      case 'amount_with_comma_separators':
        return thousand(value, ',');
      case 'amount_no_decimals_with_comma_separators':
        return thousandNoDec(value, ',');
      case 'amount_with_apostrophe_separator':
        return thousand(value, "'");
      case 'amount_no_decimals_with_space_separator':
        return thousandNoDec(value, ' ');
      case 'amount_with_space_separator':
        return thousand(value, ' ');
      case 'amount_with_period_and_space_separator':
        return thousand(value, ' ').replace('.', ',');
      default:
        return value.toFixed(2);
    }
  }
};

/* ---------- Pub/Sub ---------- */
Jomi.bus = (() => {
  const listeners = {};
  return {
    on(event, fn) {
      (listeners[event] = listeners[event] || []).push(fn);
      return () => {
        listeners[event] = (listeners[event] || []).filter(l => l !== fn);
      };
    },
    emit(event, data) {
      (listeners[event] || []).forEach(fn => {
        try { fn(data); } catch (e) { console.error('[Jomi.bus]', e); }
      });
    }
  };
})();

/* ---------- Cart API (Shopify Ajax API wrappers) ---------- */
Jomi.cart = {
  async get() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  },
  async add(formData) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Could not add to cart');
    }
    return res.json();
  },
  async change({ line, quantity, id }) {
    const body = {};
    if (line != null) body.line = line;
    if (id != null) body.id = id;
    body.quantity = quantity;
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
    return res.json();
  },
  // Shared toast for PDP ATC + product-card quick-add. Aligned behavior
  // per v38: skip drawer auto-open, show toast, keep user browsing.
  showAddedToast(item) {
    let toast = document.querySelector('[data-jomi-atc-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.setAttribute('data-jomi-atc-toast', '');
      toast.className = 'jomi-atc-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    const title = (item && (item.product_title || item.title)) || 'Item';
    const esc = (s) => (s || '').replace(/[<>&"]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
    toast.innerHTML =
      '<span class="jomi-atc-toast__check" aria-hidden="true">✓</span>' +
      '<span class="jomi-atc-toast__msg"><strong>' + esc(title) + '</strong> added to bag</span>' +
      '<a href="/cart" class="jomi-atc-toast__link">View bag →</a>';
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(Jomi.cart._toastTimer);
    Jomi.cart._toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3500);
  },
  _toastTimer: null
};

/* ---------- Quantity stepper custom element ---------- */
class JomiQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;
    this.querySelectorAll('button').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const delta = btn.dataset.action === 'plus' ? 1 : -1;
        const min = parseInt(this.input.min || '1', 10);
        const next = Math.max(min, (parseInt(this.input.value, 10) || min) + delta);
        if (next !== parseInt(this.input.value, 10)) {
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }
}
if (!customElements.get('jomi-quantity')) {
  customElements.define('jomi-quantity', JomiQuantity);
}

/* ---------- Header cart count updater ---------- */
Jomi.bus.on('cart:updated', (cart) => {
  document.querySelectorAll('[data-jomi-cart-count-display]').forEach(el => {
    el.textContent = cart.item_count;
    el.setAttribute('data-count', cart.item_count);
  });
});

/* On first load, if there's a stored count from the serialized cart, broadcast it */
document.addEventListener('DOMContentLoaded', () => {
  const seed = document.querySelector('script[data-jomi-cart-count]');
  if (seed) {
    const count = parseInt(seed.textContent || '0', 10);
    Jomi.bus.emit('cart:count', count);
    document.querySelectorAll('[data-jomi-cart-count-display]').forEach(el => {
      el.textContent = count;
    });
  }
});


/* ===========================================================================
   <jomi-quick-add> — Quick add to cart without page reload (Session 3)
   =========================================================================== */

if (!customElements.get('jomi-quick-add')) {
  customElements.define('jomi-quick-add', class extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      if (!form) return;

      const button = form.querySelector('button[type="submit"]');
      const originalLabel = button?.querySelector('span')?.textContent;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!button) return;

        button.disabled = true;
        const labelEl = button.querySelector('span');
        if (labelEl) labelEl.textContent = 'Adding…';

        try {
          const formData = new FormData(form);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: { 'Accept': 'application/json' }
          });

          if (!res.ok) throw new Error('Add to cart failed');
          const item = await res.json();

          // Refresh cart count
          const countRes = await fetch('/cart.js');
          const cart = await countRes.json();
          document.querySelectorAll('[data-jomi-cart-count-display]').forEach(el => {
            el.textContent = cart.item_count;
          });

          // Emit cart:updated so ship-bar + bag aria-label + any other listeners re-render
          Jomi.bus.emit('cart:updated', cart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

          // Aligned with PDP behavior: show toast, stay on page. No drawer auto-open.
          Jomi.cart.showAddedToast(item);

          if (labelEl) labelEl.textContent = 'Added ✓';
          setTimeout(() => {
            if (labelEl && originalLabel) labelEl.textContent = originalLabel;
            button.disabled = false;
          }, 1400);

        } catch (err) {
          console.error('Quick add failed:', err);
          if (labelEl) labelEl.textContent = 'Try again';
          setTimeout(() => {
            if (labelEl && originalLabel) labelEl.textContent = originalLabel;
            button.disabled = false;
          }, 2000);
        }
      });
    }
  });
}

/* ===========================================================================
   Localization form — auto-submit on country change (v16)
   =========================================================================== */
if (!customElements.get('jomi-localization-form')) {
  customElements.define('jomi-localization-form', class extends HTMLElement {
    connectedCallback() {
      const select = this.querySelector('select[name="country_code"]');
      if (!select) return;
      select.addEventListener('change', () => {
        this.querySelector('form')?.submit();
      });
    }
  });
}

/* ===========================================================================
   PDP Lightbox behavior (v19)
   =========================================================================== */
(function() {
  document.addEventListener('DOMContentLoaded', function() {
    var lightbox = document.querySelector('[data-jomi-lightbox]');
    var trigger = document.querySelector('[data-jomi-zoom-trigger]');
    if (!lightbox || !trigger) return;

    var images = lightbox.querySelectorAll('[data-lightbox-index]');
    var current = lightbox.querySelector('[data-current]');
    var total = lightbox.querySelector('[data-total]');
    var closeBtn = lightbox.querySelector('[data-jomi-lightbox-close]');
    var prevBtn = lightbox.querySelector('[data-jomi-lightbox-prev]');
    var nextBtn = lightbox.querySelector('[data-jomi-lightbox-next]');
    var activeIndex = 0;

    function show(idx) {
      idx = ((idx % images.length) + images.length) % images.length;
      activeIndex = idx;
      images.forEach(function(img, i) {
        img.classList.toggle('is-active', i === idx);
      });
      if (current) current.textContent = idx + 1;
    }

    function open() {
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      show(0);
      closeBtn.focus();
    }

    function close() {
      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      trigger.focus();
    }

    trigger.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) prevBtn.addEventListener('click', function() { show(activeIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function() { show(activeIndex + 1); });

    document.addEventListener('keydown', function(e) {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(activeIndex - 1);
      else if (e.key === 'ArrowRight') show(activeIndex + 1);
    });
  });
})();

/* ===========================================================================
   Cart drawer note sync (v19)
   =========================================================================== */
(function() {
  document.addEventListener('DOMContentLoaded', function() {
    var noteInputs = document.querySelectorAll('[data-jomi-drawer-note]');
    noteInputs.forEach(function(input) {
      var debounceId;
      input.addEventListener('input', function() {
        clearTimeout(debounceId);
        debounceId = setTimeout(function() {
          var formData = new FormData();
          formData.append('note', input.value);
          fetch('/cart/update.js', {
            method: 'POST',
            body: formData,
            headers: { 'Accept': 'application/json' }
          }).catch(function(err) { console.warn('[JOMI] cart note sync failed:', err); });
        }, 500);
      });
    });
  });
})();

/* ===========================================================================
   Sticky header scroll state (v20)
   =========================================================================== */
(function() {
  var header = document.querySelector('.jomi-header');
  if (!header) return;
  var lastY = 0;
  function onScroll() {
    var y = window.scrollY;
    if (y > 10) header.classList.add('is-scrolled');
    else header.classList.remove('is-scrolled');
    lastY = y;
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

/* ===========================================================================
   Featured collection carousel (v22)
   CSS-only scroll-snap drives the scroll; JS adds arrow buttons + end detection
   =========================================================================== */
(function() {
  function initCarousel(carousel) {
    var track = carousel.querySelector('[data-jomi-carousel-track]');
    var prevBtn = carousel.querySelector('[data-jomi-carousel-prev]');
    var nextBtn = carousel.querySelector('[data-jomi-carousel-next]');
    if (!track) return;

    function getStep() {
      // Scroll by one card width + gap
      var firstSlide = track.querySelector('.jomi-fc__slide');
      if (!firstSlide) return track.clientWidth * 0.8;
      var slideRect = firstSlide.getBoundingClientRect();
      var trackStyle = window.getComputedStyle(track);
      var gap = parseFloat(trackStyle.columnGap || trackStyle.gap || '0');
      return slideRect.width + gap;
    }

    function updateArrowState() {
      if (!prevBtn && !nextBtn) return;
      var atStart = track.scrollLeft <= 2;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      if (prevBtn) prevBtn.setAttribute('data-end', atStart ? 'true' : 'false');
      if (nextBtn) nextBtn.setAttribute('data-end', atEnd ? 'true' : 'false');
      carousel.setAttribute('data-at-end', atEnd ? 'true' : 'false');

      // Update counter (only present for single-card-with-arrows style)
      var counterCurrent = carousel.querySelector('[data-carousel-current]');
      if (counterCurrent) {
        var slides = track.querySelectorAll('.jomi-fc__slide');
        if (slides.length) {
          var step = getStep();
          var idx = Math.round(track.scrollLeft / step);
          idx = Math.max(0, Math.min(slides.length - 1, idx));
          counterCurrent.textContent = idx + 1;
        }
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        track.scrollBy({ left: -getStep(), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        track.scrollBy({ left: getStep(), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', updateArrowState, { passive: true });
    window.addEventListener('resize', updateArrowState, { passive: true });
    updateArrowState();
  }

  function initAll() {
    document.querySelectorAll('[data-jomi-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init after section renders (theme editor)
  document.addEventListener('shopify:section:load', function(e) {
    var carousels = e.target.querySelectorAll('[data-jomi-carousel]');
    carousels.forEach(initCarousel);
  });
})();

/* ===========================================================================
   Prefetch-on-intent (v33)
   Speeds up navigation by prefetching links on hover/touchstart, so by the time
   the user actually clicks, the HTML is already in the browser cache.

   Skips: external links, hash anchors, mailto/tel, cart/checkout, POST forms.
   Uses <link rel="prefetch"> for cross-browser compatibility (Chrome, Safari, FF).
   =========================================================================== */
(function() {
  var prefetched = new Set();
  var prefetchLimit = 30; // Cap to avoid memory churn
  var origin = window.location.origin;
  var currentPath = window.location.pathname;

  function shouldPrefetch(url) {
    if (!url) return false;
    try {
      var u = new URL(url, origin);
      if (u.origin !== origin) return false; // external
      if (u.hash && u.pathname === currentPath) return false; // hash anchor on same page
      var path = u.pathname.toLowerCase();
      // Skip cart/checkout/account/logout — stateful endpoints
      if (path.indexOf('/cart') === 0) return false;
      if (path.indexOf('/checkout') === 0) return false;
      if (path.indexOf('/account/logout') === 0) return false;
      // Skip ajax endpoints
      if (path.indexOf('.js') > 0 || path.indexOf('.json') > 0) return false;
      return true;
    } catch (e) { return false; }
  }

  function prefetch(url) {
    if (prefetched.has(url)) return;
    if (prefetched.size >= prefetchLimit) return;
    if (!shouldPrefetch(url)) return;
    prefetched.add(url);
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    document.head.appendChild(link);
  }

  function handleIntent(e) {
    var link = e.target.closest('a[href]');
    if (!link) return;
    if (link.target === '_blank' || link.hasAttribute('download')) return;
    // Avoid triggering on nav drawer close-triggers etc.
    if (link.dataset.noPrefetch !== undefined) return;
    prefetch(link.href);
  }

  // Desktop: hover intent
  document.addEventListener('mouseenter', handleIntent, true);
  // Mobile: touchstart intent (fires ~200ms before click completes — crucial head start)
  document.addEventListener('touchstart', handleIntent, { capture: true, passive: true });
})();

/* ===========================================================================
   Instant navigation feedback (v33 → v46)
   Adds a body class on link-click that fades a top progress bar + progress cursor.

   v46 fix: the previous implementation left the class stuck on the cart page
   when users clicked AJAX-intercepted elements (qty changes, remove buttons,
   any <a> with e.preventDefault in a handler). This version:

   1. Uses beforeunload as the authoritative "navigation is actually happening" signal.
      When beforeunload fires, real nav is guaranteed → keep the class.
      If no beforeunload fires within 500ms of click, clear it.
   2. Shortens the safety timeout to 500ms (was 1500ms) so if nothing navigates
      the visual state reverts quickly.
   3. Clears on pointerup/keyup/focusin as belt-and-braces backstops — any user
      interaction AFTER the nav should've started means it didn't.
   4. Explicitly clears on page-click-anywhere-outside-link.
   =========================================================================== */
(function() {
  var navTimer;
  var IS_NAVIGATING = 'is-navigating';

  function clearNavState() {
    document.body.classList.remove(IS_NAVIGATING);
    if (navTimer) { clearTimeout(navTimer); navTimer = null; }
  }

  function armNavState() {
    document.body.classList.add(IS_NAVIGATING);
    clearTimeout(navTimer);
    // Shorter timeout — if no real navigation signal within 500ms, it wasn't a nav.
    navTimer = setTimeout(clearNavState, 500);
  }

  document.addEventListener('click', function(e) {
    var link = e.target.closest('a[href]');
    if (!link) return;
    if (link.target === '_blank' || link.hasAttribute('download')) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    if (e.button !== 0) return;

    // Skip AJAX-intercepted elements explicitly
    if (e.defaultPrevented) return;
    if (link.hasAttribute('data-jomi-cart-remove')) return;
    if (link.hasAttribute('data-jomi-quick-view')) return;
    if (link.hasAttribute('data-jomi-saved-remove')) return;
    if (link.hasAttribute('data-no-nav')) return;
    if (link.getAttribute('href') === '#' || link.getAttribute('href') === 'javascript:void(0)') return;

    try {
      var url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.hash) return;
      armNavState();
    } catch (err) { /* swallow */ }
  }, true);

  // Authoritative: real nav is happening
  window.addEventListener('beforeunload', function() {
    // Keep the class — page is really leaving. Don't clear.
    clearTimeout(navTimer);
  });

  // Real-nav completion signals
  window.addEventListener('pageshow', clearNavState);
  window.addEventListener('popstate', clearNavState);

  // Any subsequent user interaction means they're still on this page → clear
  document.addEventListener('pointerup', function(e) {
    // Small grace period so the initial click doesn't self-clear
    setTimeout(clearNavState, 50);
  });
  document.addEventListener('keyup', function() { setTimeout(clearNavState, 50); });
  document.addEventListener('focusin', function() { setTimeout(clearNavState, 50); });

  // Visibility returning to page = they're back, clear state
  document.addEventListener('visibilitychange', function() {
    if (!document.hidden) clearNavState();
  });
})();

